import "./styles.css";
import {
  adminBulkDelete,
  adminDeleteRequest,
  ApiClientError,
  closeRequest,
  createRequest,
  deleteRequest,
  getAdminRequests,
  getRequest,
  setCommentVisibility,
  setOutcome,
  startSession,
  submitVote,
  type AdminRequestItem,
  type AdminRequestPage,
  type BureauData,
  type ExecutionOutcome,
  type VoteChoice,
} from "./api";
import { compressImage, formatFileSize } from "./image";

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("App root missing");
const appRoot: HTMLDivElement = app;

const DRAFT_KEY = "buy-bureau-draft-v1";
const ADMIN_SESSION_KEY = "buy-bureau-admin-session-v1";
const OUTCOME_LABELS: Record<ExecutionOutcome, string> = {
  obey: "已遵旨暂缓",
  defy: "本人决定抗旨",
  cooling: "继续冷静观察",
};
const VERDICT_LABELS: Record<VoteChoice, string> = {
  approve: "批准购买",
  reject: "坚决不买",
  cool: "建议冷静",
};
const PRESETS: Record<VoteChoice, Array<[string, string]>> = {
  approve: [
    ["approve-worth-it", "这钱花得值"],
    ["approve-happy", "批准这份快乐"],
    ["approve-long-wanted", "惦记很久，可以买"],
  ],
  reject: [
    ["reject-duplicate", "家里已有同类"],
    ["reject-impulse", "纯属冲动消费"],
    ["reject-overpriced", "价格配不上欲望"],
  ],
  cool: [
    ["cool-three-days", "先放购物车三天"],
    ["cool-alternative", "先找个平替"],
    ["cool-discount", "等降价再说"],
  ],
};

function html(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatMoney(cents: number): string {
  return new Intl.NumberFormat("zh-CN", { style: "currency", currency: "CNY" }).format(cents / 100);
}

function formatDate(timestamp: number | null): string {
  if (!timestamp) return "—";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(timestamp));
}

function setPage(kind: string, markup: string): void {
  document.body.dataset.page = kind;
  appRoot.innerHTML = markup;
  window.scrollTo({ top: 0, behavior: "instant" });
}

function showToast(message: string): void {
  const old = document.querySelector(".toast");
  old?.remove();
  const toast = document.createElement("div");
  toast.className = "toast";
  toast.textContent = message;
  document.body.append(toast);
  window.setTimeout(() => toast.remove(), 2600);
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "操作失败，请稍后再试";
}

type AdminStatusFilter = "all" | "open" | "closed" | "withdrawn";
type AdminAgeFilter = 0 | 30 | 60 | 90;

interface AdminState {
  key: string;
  status: AdminStatusFilter;
  olderThanDays: AdminAgeFilter;
  summary: AdminRequestPage["summary"];
  requests: AdminRequestItem[];
  nextBefore: number | null;
  selected: Set<string>;
}

let adminState: AdminState | null = null;

function adminStatusLabel(status: AdminRequestItem["status"]): string {
  if (status === "open") return "进行中";
  if (status === "closed") return "已结案";
  return "已撤回";
}

function adminVerdictLabel(item: AdminRequestItem): string {
  if (item.status === "open") return "等待会签";
  if (item.status === "withdrawn") return "无人受理";
  return VERDICT_LABELS[item.verdict ?? "cool"];
}

function renderAdminLogin(message = ""): void {
  document.title = "卷宗管理入口｜买不买审批局";
  setPage(
    "admin-login",
    `<main class="admin-login-shell">
      <header class="simple-header"><a href="/" aria-label="返回首页">局</a><span>买不买审批局</span><small>总务管理处</small></header>
      <section class="admin-login-panel">
        <p class="section-index">ADMINISTRATION · PRIVATE</p>
        <div class="admin-key-seal">管</div>
        <h1>卷宗管理入口</h1>
        <p>输入独立管理员密钥。密钥只保留在当前浏览器会话，关闭页面后自动失效。</p>
        <form id="admin-login-form">
          <label>管理员密钥<input id="admin-key" type="password" minlength="32" maxlength="128" autocomplete="current-password" required placeholder="请输入管理员密钥"></label>
          <button class="primary-action" type="submit">进入管理后台 <span>→</span></button>
          <p class="form-error" id="admin-login-error" role="alert">${html(message)}</p>
        </form>
      </section>
    </main>`,
  );
  const form = document.querySelector<HTMLFormElement>("#admin-login-form");
  const input = document.querySelector<HTMLInputElement>("#admin-key");
  form?.addEventListener("submit", (event) => {
    event.preventDefault();
    const key = input?.value.trim() ?? "";
    const button = form.querySelector<HTMLButtonElement>("button[type=submit]");
    if (button) button.disabled = true;
    void openAdminWorkspace(key).catch((error) => {
      if (button) button.disabled = false;
      const target = document.querySelector<HTMLElement>("#admin-login-error");
      if (target) target.textContent = errorMessage(error);
    });
  });
}

function adminRequestMarkup(item: AdminRequestItem, selected: boolean): string {
  const deletableInBulk = item.status !== "open";
  return `<li class="admin-request ${selected ? "is-selected" : ""}" data-slug="${html(item.slug)}">
    <label class="admin-select" title="${deletableInBulk ? "选择清理" : "进行中的卷宗不能批量清理"}">
      <input type="checkbox" data-admin-select="${html(item.slug)}" aria-label="选择${html(item.productName)}" ${selected ? "checked" : ""} ${deletableInBulk ? "" : "disabled"}>
      <span aria-hidden="true"></span>
    </label>
    <div class="admin-request-main">
      <div class="admin-request-line"><span class="admin-status ${item.status}">${adminStatusLabel(item.status)}</span><time>${formatDate(item.createdAt)}</time></div>
      <h2>${html(item.productName)}</h2>
      <p>${html(item.creatorNickname)} · ${html(formatMoney(item.priceCents))} · ${html(adminVerdictLabel(item))}</p>
      <div class="admin-request-meta"><span>${item.voteCount} 票</span><span>${item.commentCount} 条批示</span><span>${item.imageStored ? (item.imageAvailable ? "图片在库" : "图片已过展示期") : "无图片"}</span><span>卷宗至 ${formatDate(item.expiresAt)}</span></div>
    </div>
    <div class="admin-request-actions"><a href="/r/${html(item.slug)}" target="_blank" rel="noopener">查看</a><button data-admin-delete="${html(item.slug)}">删除</button></div>
  </li>`;
}

function selectedAdminItems(): AdminRequestItem[] {
  if (!adminState) return [];
  return adminState.requests.filter((item) => adminState?.selected.has(item.slug));
}

function updateAdminSelectionUi(): void {
  if (!adminState) return;
  const selected = selectedAdminItems();
  document.querySelectorAll<HTMLElement>(".admin-request[data-slug]").forEach((row) => {
    row.classList.toggle("is-selected", adminState?.selected.has(row.dataset.slug ?? "") === true);
  });
  const bar = document.querySelector<HTMLElement>("#admin-batch-bar");
  if (!bar) return;
  bar.hidden = selected.length === 0;
  const votes = selected.reduce((sum, item) => sum + item.voteCount, 0);
  const comments = selected.reduce((sum, item) => sum + item.commentCount, 0);
  const images = selected.filter((item) => item.imageStored).length;
  const summary = bar.querySelector<HTMLElement>("[data-batch-summary]");
  if (summary) summary.textContent = `已选 ${selected.length} 份 · ${votes} 票 · ${comments} 条批示 · ${images} 张图片`;
}

function renderAdminWorkspace(preserveScroll = false): void {
  if (!adminState) return;
  document.title = "卷宗清理台｜买不买审批局";
  const previousScroll = window.scrollY;
  const state = adminState;
  const requestList = state.requests
    .map((item) => adminRequestMarkup(item, state.selected.has(item.slug)))
    .join("");
  setPage(
    "admin",
    `<main class="admin-shell">
      <header class="simple-header dark admin-header"><a href="/" aria-label="返回首页">局</a><span>总务管理处</span><button id="admin-logout">退出</button></header>
      <section class="admin-intro">
        <p class="section-index">卷宗维护 · 数据删除不可恢复</p>
        <h1>卷宗清理台</h1>
        <p>筛选已结束的历史申请，确认影响范围后统一清理。进行中的卷宗只能单独删除。</p>
      </section>
      <dl class="admin-summary">
        <div><dt>全部</dt><dd>${state.summary.total}</dd></div>
        <div><dt>进行中</dt><dd>${state.summary.open}</dd></div>
        <div><dt>已结案</dt><dd>${state.summary.closed}</dd></div>
        <div><dt>已撤回</dt><dd>${state.summary.withdrawn}</dd></div>
        <div><dt>有图片记录</dt><dd>${state.summary.images}</dd></div>
      </dl>
      <section class="admin-controls" aria-label="卷宗筛选">
        <label>状态<select id="admin-status-filter">
          <option value="all" ${state.status === "all" ? "selected" : ""}>全部状态</option>
          <option value="open" ${state.status === "open" ? "selected" : ""}>进行中</option>
          <option value="closed" ${state.status === "closed" ? "selected" : ""}>已结案</option>
          <option value="withdrawn" ${state.status === "withdrawn" ? "selected" : ""}>已撤回</option>
        </select></label>
        <label>时间<select id="admin-age-filter">
          <option value="0" ${state.olderThanDays === 0 ? "selected" : ""}>不限时间</option>
          <option value="30" ${state.olderThanDays === 30 ? "selected" : ""}>30 天以前</option>
          <option value="60" ${state.olderThanDays === 60 ? "selected" : ""}>60 天以前</option>
          <option value="90" ${state.olderThanDays === 90 ? "selected" : ""}>90 天以前</option>
        </select></label>
        <button id="admin-refresh">刷新</button>
      </section>
      <section class="admin-list">
        <header><div><p class="section-index">当前筛选 · ${state.requests.length} 份</p><small>批量清理只选择本页已结束卷宗</small></div><button id="admin-select-page">选择本页已结束</button></header>
        <ul>${requestList || "<li class=\"admin-empty\"><b>没有符合条件的卷宗</b><span>可以调整状态或时间筛选。</span></li>"}</ul>
        ${state.nextBefore ? `<button class="admin-load-more" id="admin-load-more">加载更早的卷宗</button>` : ""}
      </section>
      <aside class="admin-batch-bar" id="admin-batch-bar" hidden><span data-batch-summary></span><button id="admin-bulk-delete">确认批量清理</button></aside>
      <footer class="site-footer"><span>图片 30 天 · 卷宗 180 天自动清理仍然生效</span><a href="/">返回前台</a></footer>
    </main>`,
  );
  bindAdminWorkspace();
  updateAdminSelectionUi();
  if (preserveScroll) requestAnimationFrame(() => window.scrollTo({ top: previousScroll, behavior: "instant" }));
}

async function refreshAdminWorkspace(status: AdminStatusFilter, olderThanDays: AdminAgeFilter): Promise<void> {
  if (!adminState) return;
  const page = await getAdminRequests(adminState.key, { status, olderThanDays });
  adminState = { ...adminState, status, olderThanDays, ...page, selected: new Set() };
  renderAdminWorkspace();
}

async function openAdminWorkspace(key: string): Promise<void> {
  const page = await getAdminRequests(key, { status: "all", olderThanDays: 0 });
  sessionStorage.setItem(ADMIN_SESSION_KEY, key);
  adminState = { key, status: "all", olderThanDays: 0, ...page, selected: new Set() };
  renderAdminWorkspace();
}

function deletionMessage(items: AdminRequestItem[]): string {
  const votes = items.reduce((sum, item) => sum + item.voteCount, 0);
  const comments = items.reduce((sum, item) => sum + item.commentCount, 0);
  const images = items.filter((item) => item.imageStored).length;
  return `将永久删除 ${items.length} 份卷宗、${votes} 票、${comments} 条批示和 ${images} 张图片记录，无法恢复。确定继续吗？`;
}

function bindAdminWorkspace(): void {
  if (!adminState) return;
  document.querySelector<HTMLButtonElement>("#admin-logout")?.addEventListener("click", () => {
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    adminState = null;
    renderAdminLogin();
  });
  const applyFilters = () => {
    const status = (document.querySelector<HTMLSelectElement>("#admin-status-filter")?.value ?? "all") as AdminStatusFilter;
    const olderThanDays = Number(document.querySelector<HTMLSelectElement>("#admin-age-filter")?.value ?? "0") as AdminAgeFilter;
    void refreshAdminWorkspace(status, olderThanDays).catch((error) => showToast(errorMessage(error)));
  };
  document.querySelector<HTMLSelectElement>("#admin-status-filter")?.addEventListener("change", applyFilters);
  document.querySelector<HTMLSelectElement>("#admin-age-filter")?.addEventListener("change", applyFilters);
  document.querySelector<HTMLButtonElement>("#admin-refresh")?.addEventListener("click", applyFilters);
  document.querySelectorAll<HTMLInputElement>("[data-admin-select]").forEach((checkbox) => {
    checkbox.addEventListener("change", () => {
      const slug = checkbox.dataset.adminSelect;
      if (!slug || !adminState) return;
      if (checkbox.checked) adminState.selected.add(slug);
      else adminState.selected.delete(slug);
      updateAdminSelectionUi();
    });
  });
  document.querySelector<HTMLButtonElement>("#admin-select-page")?.addEventListener("click", () => {
    if (!adminState) return;
    const eligible = adminState.requests.filter((item) => item.status !== "open");
    const allSelected = eligible.length > 0 && eligible.every((item) => adminState?.selected.has(item.slug));
    for (const item of eligible) {
      if (allSelected) adminState.selected.delete(item.slug);
      else adminState.selected.add(item.slug);
      const checkbox = document.querySelector<HTMLInputElement>(`[data-admin-select="${item.slug}"]`);
      if (checkbox) checkbox.checked = !allSelected;
    }
    updateAdminSelectionUi();
  });
  document.querySelectorAll<HTMLButtonElement>("[data-admin-delete]").forEach((button) => {
    button.addEventListener("click", () => {
      if (!adminState) return;
      const item = adminState.requests.find((entry) => entry.slug === button.dataset.adminDelete);
      if (!item || !window.confirm(deletionMessage([item]))) return;
      button.disabled = true;
      void adminDeleteRequest(item.slug, adminState.key)
        .then((result) => {
          showToast(`已删除 ${result.deleted} 份卷宗`);
          if (adminState) void refreshAdminWorkspace(adminState.status, adminState.olderThanDays);
        })
        .catch((error) => {
          button.disabled = false;
          showToast(errorMessage(error));
        });
    });
  });
  document.querySelector<HTMLButtonElement>("#admin-bulk-delete")?.addEventListener("click", () => {
    if (!adminState) return;
    const items = selectedAdminItems();
    if (items.length === 0 || !window.confirm(deletionMessage(items))) return;
    const button = document.querySelector<HTMLButtonElement>("#admin-bulk-delete");
    if (button) button.disabled = true;
    void adminBulkDelete(items.map((item) => item.slug), adminState.key)
      .then((result) => {
        showToast(`已清理 ${result.deleted} 份卷宗${result.skippedOpen ? `，跳过 ${result.skippedOpen} 份进行中卷宗` : ""}`);
        if (adminState) void refreshAdminWorkspace(adminState.status, adminState.olderThanDays);
      })
      .catch((error) => {
        if (button) button.disabled = false;
        showToast(errorMessage(error));
      });
  });
  document.querySelector<HTMLButtonElement>("#admin-load-more")?.addEventListener("click", () => {
    if (!adminState?.nextBefore) return;
    const button = document.querySelector<HTMLButtonElement>("#admin-load-more");
    if (button) button.disabled = true;
    void getAdminRequests(adminState.key, {
      status: adminState.status,
      olderThanDays: adminState.olderThanDays,
      before: adminState.nextBefore,
    })
      .then((page) => {
        if (!adminState) return;
        adminState.requests.push(...page.requests);
        adminState.nextBefore = page.nextBefore;
        renderAdminWorkspace(true);
      })
      .catch((error) => {
        if (button) button.disabled = false;
        showToast(errorMessage(error));
      });
  });
}

async function renderAdmin(): Promise<void> {
  const key = sessionStorage.getItem(ADMIN_SESSION_KEY);
  if (!key) return renderAdminLogin();
  try {
    await openAdminWorkspace(key);
  } catch (error) {
    if (error instanceof ApiClientError && (error.status === 401 || error.status === 503)) {
      sessionStorage.removeItem(ADMIN_SESSION_KEY);
      return renderAdminLogin(error.message);
    }
    throw error;
  }
}

async function shareLink(url: string, title: string): Promise<void> {
  if (navigator.share) {
    try {
      await navigator.share({ title, text: "请你前来审批这笔消费", url });
      return;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
    }
  }
  await navigator.clipboard.writeText(url);
  showToast("链接已复制，去微信群里开庭吧");
}

function renderHome(): void {
  setPage(
    "home",
    `<main class="home-shell">
      <section class="hero">
        <div class="hero-grid" aria-hidden="true"></div>
        <header class="hero-brand"><span>群聊消费公堂</span><span>NO. 001</span></header>
        <div class="hero-copy">
          <p class="eyebrow">BUY OR NOT BUREAU · 朋友会签制</p>
          <h1><span>买不买</span><span>审批局</span></h1>
          <p class="hero-lede">把想买的东西交给朋友正式审批。<br>朋友负责批示，你负责决定是否抗旨。</p>
          <a class="primary-action hero-action" href="/new">发起购买申请 <span>→</span></a>
        </div>
        <div class="hero-document" aria-label="购买申请示意文件">
          <div class="doc-rule"></div>
          <p>购买申请审批文件</p>
          <strong>这东西，<br>到底买不买？</strong>
          <div class="mock-line"><span>申请理由</span><b>我真的想要很久了</b></div>
          <div class="mock-line"><span>会签意见</span><b>等待朋友批示</b></div>
          <div class="seal">待批</div>
        </div>
        <a href="#how" class="scroll-cue">查看办案流程 ↓</a>
      </section>
      <section class="how" id="how">
        <p class="section-index">办案流程 · 三步结案</p>
        <ol>
          <li><span>01</span><div><b>提交卷宗</b><p>商品、金额和你那份很有说服力的理由。</p></div></li>
          <li><span>02</span><div><b>群友会签</b><p>批准、驳回或建议冷静，顺手留下一句批示。</p></div></li>
          <li><span>03</span><div><b>领取批复</b><p>生成一本正经的审批决定书，截图发回群里。</p></div></li>
        </ol>
        <a class="text-action" href="/new">现在开一宗新案 →</a>
      </section>
    </main>`,
  );
}

interface Draft {
  nickname?: string;
  productName?: string;
  price?: string;
  reason?: string;
  closingMode?: string;
  voteTarget?: string;
  deadlineHours?: string;
}

function readDraft(): Draft {
  try {
    return JSON.parse(localStorage.getItem(DRAFT_KEY) ?? "{}") as Draft;
  } catch {
    return {};
  }
}

function saveDraft(form: HTMLFormElement): void {
  const data = new FormData(form);
  const draft: Draft = {
    nickname: String(data.get("nickname") ?? ""),
    productName: String(data.get("productName") ?? ""),
    price: String(data.get("price") ?? ""),
    reason: String(data.get("reason") ?? ""),
    closingMode: String(data.get("closingMode") ?? "vote_target"),
    voteTarget: String(data.get("voteTarget") ?? "3"),
    deadlineHours: String(data.get("deadlineHours") ?? "6"),
  };
  localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
}

async function renderCreate(): Promise<void> {
  const [session, draft] = await Promise.all([startSession().catch(() => ({ nickname: "" })), Promise.resolve(readDraft())]);
  const selectedMode = draft.closingMode === "deadline" ? "deadline" : "vote_target";
  setPage(
    "form",
    `<main class="form-shell">
      <header class="simple-header"><a href="/" aria-label="返回首页">←</a><span>买不买审批局</span><small>申请立案</small></header>
      <section class="form-intro">
        <p class="eyebrow">PURCHASE APPLICATION</p>
        <h1>请提交你的<br>购买申请。</h1>
        <p>认真一点写，朋友才有东西可以认真吐槽。</p>
      </section>
      <form class="application-form" id="create-form">
        <section class="form-section image-section">
          <div class="section-heading"><span>附件 01</span><h2>商品证物</h2><em>选填</em></div>
          <label class="image-picker" for="image-input">
            <input id="image-input" name="image" type="file" accept="image/jpeg,image/png,image/webp,image/*">
            <span class="image-placeholder"><b>＋</b><strong>添加一张商品图片</strong><small>会自动压缩并在 30 天后销毁</small></span>
            <img id="image-preview" alt="商品图片预览" hidden>
          </label>
          <p class="image-status" id="image-status">图片不是必填项，上传失败也可以继续立案。</p>
        </section>
        <section class="form-section">
          <div class="section-heading"><span>正文 02</span><h2>申请信息</h2></div>
          <label>申请人昵称<input name="nickname" required minlength="2" maxlength="16" autocomplete="nickname" value="${html(draft.nickname || session.nickname)}" placeholder="朋友认得出的名字"></label>
          <label>商品名称<input name="productName" required maxlength="60" value="${html(draft.productName)}" placeholder="例如：一台其实并不缺的相机"></label>
          <label>申请金额<div class="money-input"><span>¥</span><input name="price" required inputmode="decimal" value="${html(draft.price)}" placeholder="0.00"></div></label>
          <label>为什么想买<textarea name="reason" required maxlength="200" rows="4" placeholder="请陈述购买必要性，不接受“就是想要”作为唯一证据。">${html(draft.reason)}</textarea><small class="counter"><span id="reason-count">${html(draft.reason?.length ?? 0)}</span>/200</small></label>
        </section>
        <section class="form-section">
          <div class="section-heading"><span>规则 03</span><h2>如何结案</h2></div>
          <div class="mode-switch" role="radiogroup" aria-label="结案方式">
            <label><input type="radio" name="closingMode" value="vote_target" ${selectedMode === "vote_target" ? "checked" : ""}><span><b>集齐人数</b><small>达到人数自动结案</small></span></label>
            <label><input type="radio" name="closingMode" value="deadline" ${selectedMode === "deadline" ? "checked" : ""}><span><b>限时会签</b><small>到点按现有票数结案</small></span></label>
          </div>
          <div class="rule-options" id="vote-target-options">
            ${[3, 5, 8].map((value) => `<label><input type="radio" name="voteTarget" value="${value}" ${String(draft.voteTarget ?? "3") === String(value) ? "checked" : ""}><span>${value} 人</span></label>`).join("")}
          </div>
          <div class="rule-options" id="deadline-options">
            ${[1, 6, 24].map((value) => `<label><input type="radio" name="deadlineHours" value="${value}" ${String(draft.deadlineHours ?? "6") === String(value) ? "checked" : ""}><span>${value} 小时</span></label>`).join("")}
          </div>
          <p class="rule-note" id="rule-note"></p>
        </section>
        <div class="submit-zone">
          <p>提交即表示同意：图片保留 30 天，申请明细保留 180 天。</p>
          <button class="primary-action" type="submit"><span class="button-label">正式提交申请</span><span>盖章立案 →</span></button>
          <p class="form-error" id="form-error" role="alert"></p>
        </div>
      </form>
    </main>`,
  );

  const form = document.querySelector<HTMLFormElement>("#create-form");
  const imageInput = document.querySelector<HTMLInputElement>("#image-input");
  const imagePreview = document.querySelector<HTMLImageElement>("#image-preview");
  const imageStatus = document.querySelector<HTMLElement>("#image-status");
  const reason = form?.elements.namedItem("reason") as HTMLTextAreaElement | null;
  const reasonCount = document.querySelector<HTMLElement>("#reason-count");
  let compressedImage: File | null = null;
  if (!form || !imageInput || !imagePreview || !imageStatus || !reason || !reasonCount) return;

  const syncRule = (): void => {
    const mode = (form.elements.namedItem("closingMode") as RadioNodeList).value;
    document.querySelector<HTMLElement>("#vote-target-options")?.toggleAttribute("hidden", mode !== "vote_target");
    document.querySelector<HTMLElement>("#deadline-options")?.toggleAttribute("hidden", mode !== "deadline");
    const note = document.querySelector<HTMLElement>("#rule-note");
    if (note) note.textContent = mode === "vote_target" ? "收到至少一票后，申请人也可以提前结案。" : "截止后不再接收新票；零票将自动撤回。";
  };
  syncRule();
  form.addEventListener("input", () => {
    saveDraft(form);
    reasonCount.textContent = String(reason.value.length);
  });
  form.addEventListener("change", syncRule);

  imageInput.addEventListener("change", () => {
    const source = imageInput.files?.[0];
    if (!source) return;
    imageStatus.textContent = `正在压缩原图 ${formatFileSize(source.size)}…`;
    imageStatus.dataset.state = "working";
    void compressImage(source)
      .then((file) => {
        compressedImage = file;
        imagePreview.src = URL.createObjectURL(file);
        imagePreview.hidden = false;
        document.querySelector(".image-placeholder")?.setAttribute("hidden", "");
        imageStatus.textContent = `已自动压缩至 ${formatFileSize(file.size)}，拍摄信息已移除。`;
        imageStatus.dataset.state = "ready";
      })
      .catch((error) => {
        compressedImage = null;
        imageInput.value = "";
        imageStatus.textContent = `${errorMessage(error)}。你仍可无图提交。`;
        imageStatus.dataset.state = "error";
      });
  });

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const submit = form.querySelector<HTMLButtonElement>("button[type=submit]");
    const formError = document.querySelector<HTMLElement>("#form-error");
    if (!submit || !formError) return;
    submit.disabled = true;
    submit.dataset.loading = "true";
    formError.textContent = "";
    const payload = new FormData(form);
    payload.delete("image");
    if (compressedImage) payload.append("image", compressedImage);
    void createRequest(payload)
      .then((created) => {
        localStorage.removeItem(DRAFT_KEY);
        localStorage.setItem(`manage:${created.slug}`, created.manageToken);
        window.location.assign(created.manageUrl);
      })
      .catch((error) => {
        formError.textContent = errorMessage(error);
        submit.disabled = false;
        delete submit.dataset.loading;
      });
  });
}

function ruleLabel(data: BureauData): string {
  if (data.request.closingMode === "vote_target") return `集齐 ${data.request.voteTarget} 人自动结案`;
  return `${formatDate(data.request.closesAt)} 截止`;
}

function requestHero(data: BureauData): string {
  const request = data.request;
  return `<section class="case-hero">
    <div class="case-number">案号 · ${html(request.slug.slice(0, 8).toUpperCase())}</div>
    <div class="evidence-frame ${request.imageAvailable ? "" : "no-image"}">
      ${request.imageAvailable ? `<img src="${html(request.imageUrl)}" alt="${html(request.productName)}">` : `<div><span>证物</span><b>暂无图片</b></div>`}
    </div>
    <div class="case-summary">
      <p>${html(request.creatorNickname)} 提交的购买申请</p>
      <h1>${html(request.productName)}</h1>
      <strong>${html(formatMoney(request.priceCents))}</strong>
      <blockquote>“${html(request.reason)}”</blockquote>
      <div class="case-rule"><span>结案规则</span><b>${html(ruleLabel(data))}</b></div>
    </div>
  </section>`;
}

function statusBanner(data: BureauData): string {
  if (data.request.status === "open") {
    return `<div class="status-strip open"><i></i><span>本案正在会签</span><b>${data.counts?.total ?? 0} 票</b></div>`;
  }
  if (data.request.status === "withdrawn") {
    return `<div class="status-strip withdrawn"><i></i><span>本案已撤回</span><b>无人受理</b></div>`;
  }
  return `<div class="status-strip closed"><i></i><span>${data.request.closedEarly ? "提前结案" : "正式结案"}</span><b>${html(VERDICT_LABELS[data.request.verdict ?? "cool"])}</b></div>`;
}

function votePanel(data: BureauData): string {
  if (data.viewer.isCreatorDevice && !data.viewer.canManage) {
    return `<section class="notice-panel"><b>这是你发起的申请</b><p>申请人不能给自己投票。请使用创建时保存的管理链接查看进度。</p></section>`;
  }
  if (data.request.status !== "open") return "";
  const selected = data.viewer.voteChoice;
  return `<section class="vote-panel">
    <p class="section-index">会签意见 · 进度与批示实时公开</p>
    <h2>${data.viewer.hasVoted ? "修改你的批示" : "请作出你的批示"}</h2>
    <label class="nickname-field">你的昵称<input id="vote-nickname" minlength="2" maxlength="16" value="${html(data.viewer.nickname)}" placeholder="让朋友知道是谁"></label>
    <div class="vote-choices" role="radiogroup">
      <label class="approve"><input type="radio" name="vote-choice" value="approve" ${selected === "approve" ? "checked" : ""}><span><small>准</small><b>批准购买</b><em>这钱可以花</em></span></label>
      <label class="reject"><input type="radio" name="vote-choice" value="reject" ${selected === "reject" ? "checked" : ""}><span><small>驳</small><b>坚决不买</b><em>立刻关闭页面</em></span></label>
      <label class="cool"><input type="radio" name="vote-choice" value="cool" ${selected === "cool" ? "checked" : ""}><span><small>缓</small><b>建议冷静</b><em>过几天再议</em></span></label>
    </div>
    <div class="preset-area" id="preset-area"><p>附一句快捷批示</p><div></div></div>
    <label class="comment-field">补充意见<textarea id="vote-comment" maxlength="200" rows="3" placeholder="选填，但通常这里最好笑。"></textarea></label>
    <button class="primary-action" id="submit-vote">${data.viewer.hasVoted ? "更新审批意见" : "提交审批意见"}<span>落笔生效 →</span></button>
    <p class="form-error" id="vote-error" role="alert"></p>
  </section>`;
}

function commentsSection(data: BureauData, management: boolean): string {
  const comments = data.comments
    .map(
      (comment) => `<li class="${comment.visible ? "" : "hidden-comment"}">
        <span class="comment-stamp ${comment.choice}">${comment.choice === "approve" ? "准" : comment.choice === "reject" ? "驳" : "缓"}</span>
        <div><b>${html(comment.nickname)}</b>${comment.presetLabel ? `<em>${html(comment.presetLabel)}</em>` : ""}${comment.body ? `<p>${html(comment.body)}</p>` : ""}${!comment.visible ? `<small>此批示已隐藏</small>` : ""}</div>
        ${management ? `<button class="comment-toggle" data-comment-id="${html(comment.id)}" data-visible="${comment.visible}">${comment.visible ? "隐藏" : "恢复"}</button>` : ""}
      </li>`,
    )
    .join("");
  return `<section class="comments-section"><p class="section-index">朋友批示 · ${data.comments.length} 条</p><ul>${comments || "<li class=\"empty-comment\">尚无朋友留下文字批示，等你来落第一笔。</li>"}</ul></section>`;
}

function resultDocument(data: BureauData, management: boolean): string {
  const counts = data.counts ?? { approve: 0, reject: 0, cool: 0, total: 0 };
  const closed = data.request.status !== "open";
  const verdict = data.request.verdict ?? "cool";
  return `<section class="result-wrap ${closed ? "is-final" : "is-live"}">
    <div class="official-document">
      <header><p>买不买审批局 · 购买申请批复</p><div></div><small>群审字〔${new Date(data.request.createdAt).getFullYear()}〕${html(data.request.slug.slice(0, 6).toUpperCase())}号</small></header>
      <h2>${closed ? `关于${html(data.request.creatorNickname)}同志购买` : "当前会签进度"}<br>${html(data.request.productName)}${closed ? "的批复" : ""}</h2>
      <p class="document-lede">经朋友群体审议，本局现将${closed ? "最终" : "阶段性"}意见公布如下：</p>
      <div class="vote-tally">
        <div><span>准</span><b>${counts.approve}</b><small>批准购买</small></div>
        <div><span>驳</span><b>${counts.reject}</b><small>坚决不买</small></div>
        <div><span>缓</span><b>${counts.cool}</b><small>建议冷静</small></div>
      </div>
      ${closed && data.request.status === "closed" ? `<div class="final-verdict"><span>审批结论</span><strong>${html(VERDICT_LABELS[verdict])}</strong><p>${data.request.closedEarly ? "申请人选择提前结案，以上结论按现有票数形成。" : "以上结论依据相对多数形成；平票时按建议冷静处理。"}</p><div class="final-seal">${verdict === "approve" ? "准予购买" : verdict === "reject" ? "不予批准" : "暂缓处理"}</div></div>` : ""}
      ${data.request.status === "withdrawn" ? `<div class="final-verdict withdrawn"><span>结案说明</span><strong>无人受理，本案撤回</strong><p>截止时间内未收到有效会签意见，不形成购买建议。</p></div>` : ""}
      <footer><span>决定日期</span><b>${formatDate(data.request.closedAt ?? Date.now())}</b></footer>
    </div>
    ${commentsSection(data, management)}
    ${closed && data.request.status === "closed" ? `<section class="outcome-public"><span>申请人执行回执</span><b>${data.request.executionOutcome ? html(OUTCOME_LABELS[data.request.executionOutcome]) : "等待申请人表态"}</b></section>` : ""}
  </section>`;
}

function shareActions(data: BureauData): string {
  if (data.request.status === "open") return "";
  return `<section class="share-result"><p>审批结束，公文可以截图了。</p><button id="share-result" class="primary-action">再次分享到群里 <span>→</span></button><a href="/new">我也发起一个</a></section>`;
}

function renderCasePage(data: BureauData, management: boolean): void {
  setPage(
    management ? "manage" : "case",
    `<main class="case-shell">
      <header class="simple-header dark"><a href="/" aria-label="返回首页">局</a><span>买不买审批局</span><small>${management ? "申请人管理处" : "朋友会签处"}</small></header>
      ${requestHero(data)}
      ${statusBanner(data)}
      ${management ? managementPanel(data) : votePanel(data)}
      ${resultDocument(data, management)}
      ${shareActions(data)}
      <footer class="site-footer"><span>图片保存 30 天 · 申请明细保存 180 天</span><a href="/new">另立一案</a></footer>
    </main>`,
  );
  bindCaseInteractions(data, management);
}

function managementPanel(data: BureauData): string {
  const publicUrl = `${location.origin}/r/${data.request.slug}`;
  const canClose = data.request.status === "open" && data.request.closingMode === "vote_target" && (data.counts?.total ?? 0) > 0;
  return `<section class="management-panel">
    <p class="section-index">申请人管理处</p>
    <h2>把审批链接发给朋友</h2>
    <button class="primary-action" id="share-public" data-url="${html(publicUrl)}">分享公开审批链接 <span>→</span></button>
    <div class="manage-actions">
      ${canClose ? `<button id="close-early">按现有 ${data.counts?.total ?? 0} 票提前结案</button>` : ""}
      ${data.request.status === "closed" ? `<div class="outcome-picker"><p>提交执行回执</p>${(["obey", "defy", "cooling"] as const).map((outcome) => `<button data-outcome="${outcome}" class="${data.request.executionOutcome === outcome ? "active" : ""}">${html(OUTCOME_LABELS[outcome])}</button>`).join("")}</div>` : ""}
      <button class="danger-link" id="delete-request">撤回并删除整份申请</button>
    </div>
  </section>`;
}

function bindVoteControls(data: BureauData): void {
  const choices = Array.from(document.querySelectorAll<HTMLInputElement>('input[name="vote-choice"]'));
  const presetArea = document.querySelector<HTMLElement>("#preset-area div");
  const refreshPresets = (): void => {
    const choice = choices.find((input) => input.checked)?.value as VoteChoice | undefined;
    if (!presetArea) return;
    presetArea.innerHTML = choice
      ? PRESETS[choice].map(([code, label]) => `<label><input type="radio" name="preset" value="${code}"><span>${html(label)}</span></label>`).join("")
      : "<small>先选择批准、驳回或冷静。</small>";
  };
  choices.forEach((choice) => choice.addEventListener("change", refreshPresets));
  refreshPresets();

  document.querySelector<HTMLButtonElement>("#submit-vote")?.addEventListener("click", (event) => {
    const button = event.currentTarget as HTMLButtonElement;
    const nickname = document.querySelector<HTMLInputElement>("#vote-nickname")?.value ?? "";
    const choice = choices.find((input) => input.checked)?.value as VoteChoice | undefined;
    const presetCode = document.querySelector<HTMLInputElement>('input[name="preset"]:checked')?.value ?? "";
    const comment = document.querySelector<HTMLTextAreaElement>("#vote-comment")?.value ?? "";
    const errorNode = document.querySelector<HTMLElement>("#vote-error");
    if (!choice) {
      if (errorNode) errorNode.textContent = "请先作出批准、驳回或冷静的选择。";
      return;
    }
    button.disabled = true;
    if (errorNode) errorNode.textContent = "";
    void submitVote(data.request.slug, { nickname, choice, presetCode, comment })
      .then((updated) => {
        renderCasePage(updated, false);
        showToast("你的批示已经生效");
      })
      .catch((error) => {
        if (errorNode) errorNode.textContent = errorMessage(error);
        button.disabled = false;
      });
  });
}

function bindCaseInteractions(data: BureauData, management: boolean): void {
  if (!management) bindVoteControls(data);
  const publicUrl = `${location.origin}/r/${data.request.slug}`;
  document.querySelector<HTMLButtonElement>("#share-result")?.addEventListener("click", () => {
    void shareLink(publicUrl, `${data.request.creatorNickname} 的购买审批结果`).catch((error) => showToast(errorMessage(error)));
  });
  if (!management) return;
  const token = localStorage.getItem(`manage:${data.request.slug}`) ?? "";
  document.querySelector<HTMLButtonElement>("#share-public")?.addEventListener("click", () => {
    void shareLink(publicUrl, `${data.request.creatorNickname} 想买「${data.request.productName}」`).catch((error) => showToast(errorMessage(error)));
  });
  document.querySelector<HTMLButtonElement>("#close-early")?.addEventListener("click", (event) => {
    const button = event.currentTarget as HTMLButtonElement;
    button.disabled = true;
    void closeRequest(data.request.slug, token)
      .then((updated) => {
        renderCasePage(updated, true);
        showToast("已经按现有票数提前结案");
      })
      .catch((error) => {
        showToast(errorMessage(error));
        button.disabled = false;
      });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-outcome]").forEach((button) => {
    button.addEventListener("click", () => {
      const outcome = button.dataset.outcome as ExecutionOutcome;
      void setOutcome(data.request.slug, token, outcome)
        .then((updated) => {
          renderCasePage(updated, true);
          showToast("执行回执已公开");
        })
        .catch((error) => showToast(errorMessage(error)));
    });
  });
  document.querySelectorAll<HTMLButtonElement>(".comment-toggle").forEach((button) => {
    button.addEventListener("click", () => {
      const id = button.dataset.commentId;
      const visible = button.dataset.visible === "true";
      if (!id) return;
      void setCommentVisibility(data.request.slug, id, token, !visible)
        .then((updated) => renderCasePage(updated, true))
        .catch((error) => showToast(errorMessage(error)));
    });
  });
  document.querySelector<HTMLButtonElement>("#delete-request")?.addEventListener("click", () => {
    if (!window.confirm("确定删除整份申请吗？图片、投票和评论都会立即删除，无法恢复。")) return;
    void deleteRequest(data.request.slug, token)
      .then(() => {
        localStorage.removeItem(`manage:${data.request.slug}`);
        window.location.assign("/");
      })
      .catch((error) => showToast(errorMessage(error)));
  });
}

function loadingPage(): void {
  setPage("loading", `<main class="loading-page"><div class="loading-seal">审</div><p>正在调取卷宗…</p></main>`);
}

function errorPage(error: unknown): void {
  const message = error instanceof ApiClientError && error.status === 404 ? "这份申请不存在或已经到期销毁。" : errorMessage(error);
  setPage("error", `<main class="error-page"><div>退</div><h1>卷宗调取失败</h1><p>${html(message)}</p><a class="primary-action" href="/">返回审批局</a></main>`);
}

function extractSlug(pathname: string): string | null {
  return pathname.match(/^\/(?:r|manage)\/([A-Za-z0-9_-]{20,40})$/)?.[1] ?? null;
}

async function renderRequestRoute(management: boolean): Promise<void> {
  const slug = extractSlug(location.pathname);
  if (!slug) return errorPage(new Error("链接格式不正确"));
  loadingPage();
  if (management) {
    const fragmentToken = new URLSearchParams(location.hash.slice(1)).get("token");
    if (fragmentToken) {
      localStorage.setItem(`manage:${slug}`, fragmentToken);
      history.replaceState(null, "", location.pathname);
    }
  }
  const token = management ? localStorage.getItem(`manage:${slug}`) ?? "" : undefined;
  if (management && !token) throw new Error("管理凭证已丢失，请使用创建时保存的管理链接重新打开。 ");
  const data = await getRequest(slug, token);
  renderCasePage(data, management);
}

async function renderRoute(): Promise<void> {
  if (location.pathname === "/" || location.pathname === "/index.html") return renderHome();
  if (location.pathname === "/new") return renderCreate();
  if (location.pathname === "/admin") return renderAdmin();
  if (location.pathname.startsWith("/r/")) return renderRequestRoute(false);
  if (location.pathname.startsWith("/manage/")) return renderRequestRoute(true);
  errorPage(new Error("页面不存在"));
}

void renderRoute().catch(errorPage);
